/**
 * Date formatting helpers shared across products.
 *
 * All functions take an explicit `timezone` argument so the UI library stays
 * decoupled from any product-specific brand context. Each consumer is expected
 * to wrap these in a thin hook that supplies the brand timezone.
 *
 * Moteur : la conversion instant -> heure murale vient de `./timezone`
 * (donnees de fuseaux embarquees, independantes du navigateur). L'heure murale
 * est ensuite portee par un `UTCDate` (ses accesseurs locaux lisent les champs
 * UTC) : `date-fns` formate donc les motifs sans jamais consulter le fuseau de
 * la machine.
 */

import { format, formatDistanceToNow, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { UTCDate } from '@date-fns/utc';
import { toWallClock } from './timezone';

export type DateInput = string | Date | null | undefined;

function toInstant(date: DateInput): Date | null {
  if (!date) return null;
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return isValid(dateObj) ? dateObj : null;
}

/** Heure murale du fuseau, portee par un `UTCDate` pour le formatage. */
function toZonedDate(date: DateInput, timezone: string): Date | null {
  const instant = toInstant(date);
  if (!instant) return null;
  const wall = toWallClock(instant, timezone);
  if (!wall) return null;
  return new UTCDate(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second,
    wall.millisecond
  );
}

/** "15 mars 2024" */
export function formatDate(date: DateInput, timezone: string, formatStr = 'd MMMM yyyy'): string {
  const zoned = toZonedDate(date, timezone);
  if (!zoned) return '-';
  return format(zoned, formatStr, { locale: fr });
}

/** "15/03/2024" */
export function formatDateShort(date: DateInput, timezone: string): string {
  return formatDate(date, timezone, 'dd/MM/yyyy');
}

/** "14 fev. 2026" */
export function formatLongDate(date: DateInput, timezone: string): string {
  return formatDate(date, timezone, 'd MMM yyyy');
}

/** "15 mars 2024 a 14:30" */
export function formatDateTime(date: DateInput, timezone: string): string {
  return formatDate(date, timezone, "d MMMM yyyy 'a' HH:mm");
}

/** "15/03/2024 14:30" */
export function formatDateTimeShort(date: DateInput, timezone: string): string {
  return formatDate(date, timezone, 'dd/MM/yyyy HH:mm');
}

/** "14:30" */
export function formatTime(date: DateInput, timezone: string): string {
  return formatDate(date, timezone, 'HH:mm');
}

/**
 * "il y a 2 heures".
 *
 * Une duree ecoulee ne depend d'aucun fuseau : on compare l'instant reel a
 * maintenant. `timezone` est conserve pour la signature (hooks consommateurs).
 */
export function formatRelative(date: DateInput, _timezone: string): string {
  const instant = toInstant(date);
  if (!instant) return '-';
  return formatDistanceToNow(instant, { addSuffix: true, locale: fr });
}

/**
 * Formate un jour civil (`YYYY-MM-DD`, sans fuseau) avec un motif date-fns.
 * Ex. `formatNaiveDate('2026-06-05', 'EEEE d MMMM yyyy')` -> "vendredi 5 juin 2026".
 */
export function formatNaiveDate(naiveDate: string | null | undefined, formatStr = 'dd/MM/yyyy'): string {
  const match = naiveDate ? /^(\d{4})-(\d{2})-(\d{2})/.exec(naiveDate) : null;
  if (!match) return '-';
  const civil = new UTCDate(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12);
  if (!isValid(civil)) return '-';
  return format(civil, formatStr, { locale: fr });
}

/**
 * Format a Date as a naive YYYY-MM-DD string (no timezone shift).
 *
 * Used to send date filters to the backend. Do NOT use `toISOString()` because
 * it shifts by one day in positive timezones (e.g. Europe/Paris).
 *
 * Convention : le `Date` recu est un jour civil choisi dans un calendrier
 * (ex. valeur du DatePicker sans heure), porte par ses champs locaux.
 */
export function formatDateNaive(date: Date | null | undefined): string | null {
  if (!date || !isValid(date)) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
